import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Stack, Tooltip, Typography } from '@mui/material';
import { ColDef, GetRowIdParams, IsFullWidthRowParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { GridWrapper } from '../../../components/AgTable/AgTable';
import { AgTableWithBaseStyle } from '../../../components/AgTable/AgTable2';
import { CapitalAllocation } from '../../../schemas/FundCapitalAllocation.schema';
import { FundViewModel, fundViewModelSchema } from '../../../schemas/FundViewModel2.schema';
import { agBorderColor } from '../../../theme/ag-foresight';
import { colors } from '../../../theme/colors';
import { FMT } from '../../../util/formatter-service';
import { schemaToFormFields } from '../../../util/schema-utils';
import { MenuButton } from '../../Finance2/common-grid-defs/MenuButton';
import { selectedFundStateFP, showProjectionFormState } from '../state/FPState';
import { StringFormatterId } from '../../../util/formatters/StringFormatters';
import {
  CapitalAllocationRowData,
  fundCapitalRows,
  FundAllocationField,
  FundFields,
  getCapitalAllocationColDefs,
  getCapitalAllocationRowData,
  NameColumnWidth,
  shortUsdWithDecimals,
} from './capitalAllocationGridDefs';

const fixedCellWidth = 200; // we need this for committed capital full width row alignment
const defaultColDef: ColDef = { minWidth: fixedCellWidth, maxWidth: fixedCellWidth, resizable: false };
const tooltipShowDelay = 1000;

export function CapitalAllocationGrid({ data }: { data?: CapitalAllocation[] | null }) {
  const rowData = useMemo(() => {
    if (!data) {
      return;
    }
    return getCapitalAllocationRowData(data);
  }, [data]);
  const colDefs = useMemo(() => {
    if (!data) {
      return [];
    }
    return getCapitalAllocationColDefs(data);
  }, [data]);

  const getRowId = useCallback((params: GetRowIdParams<CapitalAllocationRowData>) => params.data?.field, []);
  const committedCapitalData = useMemo(() => fundCapitalRows(), []);

  return (
    <>
      <Stack direction='row' alignItems={'center'} justifyContent={'space-between'} mb={'0.2rem'}>
        <Typography>Fund Allocation</Typography>
        <AllocationActions />
      </Stack>
      <div css={GridWrapper} style={{ height: '70vh' }}>
        <AgTableWithBaseStyle
          className='ag-theme-1 ag-theme-foresight'
          rowData={rowData}
          columnDefs={colDefs}
          getRowId={getRowId}
          isFullWidthRow={isCommittedCapitalRow}
          fullWidthCellRenderer={FundAllocationDataRowRenderer}
          pinnedTopRowData={committedCapitalData}
          defaultColDef={defaultColDef}
          groupDisplayType='groupRows'
          groupDefaultExpanded={-1}
          rowGroupPanelShow='never'
          tooltipShowDelay={tooltipShowDelay}
        />
      </div>
    </>
  );
}

function isCommittedCapitalRow(params: IsFullWidthRowParams<CapitalAllocationRowData>) {
  return FundFields.has(params.rowNode?.data?.field as FundAllocationField);
}

function AllocationActions() {
  const setShowForm = useSetAtom(showProjectionFormState);
  const menuItems = useMemo(() => {
    return [
      {
        name: 'Edit Fees &  Expenses',
        onClick: () => setShowForm('feesAndExpenses'),
        icon: <EditOutlinedIcon />,
      },
    ];
  }, [setShowForm]);

  return <MenuButton menuItems={menuItems} label='edit allocation data' />;
}

export function FundAllocationDataRowRenderer(params: CustomCellRendererProps<CapitalAllocationRowData>) {
  const { data } = params;
  const fund = useAtomValue(selectedFundStateFP);
  const shortFormatter = useMemo(() => shortUsdWithDecimals(), []);
  if (!data || !fund) {
    return null;
  }
  const formField = schemaToFormFields(fundViewModelSchema(), [data.field as keyof FundViewModel])[0];

  return (
    <Stack height='100%' direction='row' sx={{ background: colors.secondary[5] }}>
      <Typography
        variant='body2'
        pl='1rem'
        height='100%'
        display='flex'
        alignItems='center'
        minWidth={`${NameColumnWidth}px`}
        maxWidth={`${NameColumnWidth}px`}
        sx={{ borderRight: `1px solid ${agBorderColor}` }}
      >
        {`${formField.label || data.name}`}
      </Typography>
      <Tooltip
        title={FMT.format(formField.formatter ?? StringFormatterId.string, data.value)}
        slotProps={{
          tooltip: {
            // make it similar to default ag-grid tooltip used for regular cells
            sx: {
              padding: '0.875rem',
              fontSize: '0.875rem',
            },
          },
        }}
        enterDelay={tooltipShowDelay}
      >
        <Typography
          variant='body2'
          height='100%'
          display='flex'
          alignItems='center'
          minWidth='200px'
          maxWidth='200px'
          justifyContent={'flex-end'}
          pr='1rem'
        >
          {FMT.format(shortFormatter, data.value)}
        </Typography>
      </Tooltip>
    </Stack>
  );
}
